import { stagger } from "animejs";
import { useId } from "react";

import {
  BAR_W,
  barX,
  BASELINE,
  cost,
  costSeries,
  H,
  linePath,
  net,
  netSeries,
  PAD_L,
  PAD_R,
  PRICE,
  revenue,
  TECH,
  W,
  x,
  y,
} from "@/3-features/solutions/hedgeModel";
import {
  handles,
  prefersReducedMotion,
  useLoopInView,
} from "@/4-components/animations/anime";
import { GREEN, INK } from "@/5-shared/constants/palette";

import { BentoArt, bentoLoop } from "./BentoArt";

/**
 * Copertura naturale, the punchline of `HedgeStory`, in the tile that leads to it.
 * The energy cost draws in, jumping from year to year; the net cost draws in flat
 * underneath; then the plant revenues rise from the baseline and go and fill exactly
 * the gap between the two. It works because `ricavi = spesa − netto`, so the lift is a
 * pure translation. Same maths as the scene (`hedgeModel`), without axes, grid, legend
 * or figures.
 */

const D = TECH.eolico;
const COSTS = costSeries();
const NETS = netSeries(D.revBase, D.beta);
/** how far each bar rises from the baseline to hang between the two lines (px, negative) */
const LIFT = PRICE.map((p) => y(cost(p)) - y(revenue(p, D.revBase, D.beta)));
/** opacity of a bar once it is suspended: the two lines below it define the gap */
const BAR_ALPHA = 0.5;

/** the plot area alone, the scene's gutters hold axis labels this thumbnail has none of */
const VIEW_X = PAD_L - 8;
const VIEW_W = W - PAD_L - PAD_R + 16;
const VIEW = `${VIEW_X} 40 ${VIEW_W} ${BASELINE - 30}`;

export function HedgeMini() {
  const clipId = `hm-wipe-${useId().replace(/:/g, "")}`;
  const ref = useLoopInView<HTMLDivElement>((root) => {
    const { one, all } = handles(root);
    const costWipe = one<SVGRectElement>(".hm-cost-wipe");
    const netWipe = one<SVGRectElement>(".hm-net-wipe");
    const bars = all<SVGRectElement>(".hm-bar");
    if (!costWipe || !netWipe || bars.length === 0) return;

    const cost = { width: 0 };
    const net = { width: 0 };
    const paintCost = () => costWipe.setAttribute("width", String(cost.width));
    const paintNet = () => netWipe.setAttribute("width", String(net.width));
    paintCost();
    paintNet();

    if (prefersReducedMotion()) {
      // the punchline, held still: both lines drawn, the bars filling the gap
      cost.width = net.width = VIEW_W;
      paintCost();
      paintNet();
      bars.forEach((b, i) => {
        b.style.transform = `translateY(${LIFT[i]}px) scaleY(1)`;
        b.style.opacity = String(BAR_ALPHA);
      });
      return;
    }

    return (
      bentoLoop()
        // 01, la spesa energia, che oscilla di anno in anno
        .add(cost, { width: VIEW_W, duration: 900, onUpdate: paintCost }, 300)
        // 02, i ricavi degli impianti crescono dalla base, un anno alla volta
        .add(
          bars,
          { scaleY: [0, 1], duration: 450, delay: stagger(50), ease: "out(3)" },
          1300,
        )
        // 03, salgono a colmare il divario, e sotto emerge il costo netto: piatto
        .add(
          bars,
          {
            translateY: (_el?: unknown, i = 0) => LIFT[i],
            duration: 500,
            delay: stagger(40),
            ease: "inOut(3)",
          },
          2300,
        )
        .add(
          bars,
          { opacity: [1, BAR_ALPHA], duration: 500, delay: stagger(40) },
          2300,
        )
        .add(net, { width: VIEW_W, duration: 700, onUpdate: paintNet }, 2500)
        // hold, then unwind to the start so the loop restart doesn't snap
        .add(net, { width: 0, duration: 600, onUpdate: paintNet }, 5600)
        .add(
          bars,
          {
            translateY: 0,
            opacity: [BAR_ALPHA, 1],
            duration: 500,
            delay: stagger(30),
          },
          5900,
        )
        .add(bars, { scaleY: [1, 0], duration: 450, delay: stagger(30) }, 6500)
        .add(cost, { width: 0, duration: 700, onUpdate: paintCost }, 6900)
    );
  });

  return (
    <BentoArt ref={ref} className="h-full">
      <svg viewBox={VIEW} preserveAspectRatio="none" className="h-full w-full">
        <defs>
          <clipPath id={`${clipId}-cost`} clipPathUnits="userSpaceOnUse">
            <rect
              className="hm-cost-wipe"
              x={VIEW_X}
              y={0}
              width={0}
              height={H}
            />
          </clipPath>
          <clipPath id={`${clipId}-net`} clipPathUnits="userSpaceOnUse">
            <rect
              className="hm-net-wipe"
              x={VIEW_X}
              y={0}
              width={0}
              height={H}
            />
          </clipPath>
        </defs>
        {PRICE.map((p, i) => (
          <rect
            key={i}
            className="hm-bar"
            x={barX(i)}
            y={y(revenue(p, D.revBase, D.beta))}
            width={BAR_W}
            height={Math.max(0, y(net(p, D.revBase, D.beta)) - y(cost(p)))}
            rx="3"
            fill={GREEN}
            style={{
              transformOrigin: `${x(i)}px ${BASELINE}px`,
              transform: "scaleY(0)",
            }}
          />
        ))}
        <path
          className="hm-cost"
          d={linePath(COSTS)}
          fill="none"
          stroke={INK[400]}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          clipPath={`url(#${clipId}-cost)`}
        />
        <path
          className="hm-net"
          d={linePath(NETS)}
          fill="none"
          stroke={INK[900]}
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          clipPath={`url(#${clipId}-net)`}
        />
      </svg>
    </BentoArt>
  );
}
